import { type FC, type ImgHTMLAttributes } from 'react';
import classnames from 'classnames';
import { constants } from 'ethers';
import { useFragment, graphql } from 'react-relay';
import { defaultAvatarGradient } from 'dibs-crypto-utils/exports/formattingUtils.ts';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations.ts';
import { type NftAvatarCircle_address$key } from './__generated__/NftAvatarCircle_address.graphql.ts';
import styles from './NftAvatarCircle.scss';

type Props = {
    address?: NftAvatarCircle_address$key | null | undefined;
    addressOverride?: {
        address: string;
        avatarImage?: string;
    };
    wrapperClassName?: string;
    imageClassName?: string;
    avatarSize?: number;
    loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
};

const avatarParams = (avatarSize: number | undefined = 27): string =>
    ioParams({
        height: `${avatarSize}`,
        width: `${avatarSize}`,
        dpr: '2',
        precrop: '1:1',
        'resize-filter': 'bilinear',
        format: 'pjpg',
        auto: 'webp',
    }).toQuery();

export const NftAvatarCircle: FC<Props> = ({
    address: addressRef = null,
    addressOverride = null,
    wrapperClassName,
    imageClassName,
    avatarSize,
    loading,
}) => {
    const addressObj = useFragment(
        graphql`
            fragment NftAvatarCircle_address on EthereumAddressType {
                address
                publicProfile {
                    avatarImage {
                        path
                    }
                }
            }
        `,
        addressRef
    );
    const avatarImage =
        addressObj?.publicProfile?.avatarImage?.path || addressOverride?.avatarImage;
    return (
        <div
            style={{
                background: defaultAvatarGradient(
                    addressObj?.address || addressOverride?.address || constants.AddressZero
                ),
            }}
            className={classnames(styles.wrapper, wrapperClassName)}
        >
            {!!avatarImage && (
                <img
                    loading={loading}
                    alt="avatar"
                    src={`${avatarImage}${avatarParams(avatarSize)}`}
                    className={classnames(styles.image, imageClassName)}
                />
            )}
        </div>
    );
};
